import { useState } from 'react';
import {
  Wand2, ChevronUp, ChevronDown, Trash2, Maximize2, Minimize2, Loader2, X, Pencil, Code2,
  MessageSquare,
} from 'lucide-react';
import { customDashboardsAPI } from '../../../services/api';
import { InlineLoader } from '../../loading';
import type { ChartBlockConfig, ChartKind, DashboardBlock, TextBlockConfig } from './types';

/**
 * Author controls for one block. Shown only to users who can author, and only on the
 * block they are hovering/editing.
 *
 * The split that matters: chart type, width and order are DETERMINISTIC edits (a
 * PATCH, instant, no model call), while "refine" is the only control that costs a
 * generation. Routing a chart-type switch through the model would make an instant,
 * reversible change slow and non-deterministic — and it might come back with
 * different SQL than the user already approved.
 */

const CHART_KINDS: ChartKind[] = ['bar', 'line', 'area', 'stacked_bar', 'pie'];
const CHART_LABEL: Record<ChartKind, string> = {
  bar: 'Bar',
  line: 'Line',
  area: 'Area',
  stacked_bar: 'Stacked',
  pie: 'Pie',
};

const errorMessage = (e: unknown): string => {
  const status = (e as any)?.response?.status;
  const fromServer = (e as any)?.response?.data?.message;
  if (fromServer) return fromServer;
  // A bare 502/503/504 is the GATEWAY giving up, not our API answering. Generation
  // makes sequential model calls, so a long one can outlive the ingress timeout and
  // arrive as "Request failed with status code 503" — which tells the user nothing
  // and looks like a bug in their prompt.
  if (status === 502 || status === 503 || status === 504) {
    return 'The server took too long to answer and the request was cut off before it finished. This usually means the change was too large to work out in one go — try a smaller, more specific instruction.';
  }
  return (e as any)?.message || 'Something went wrong.';
};

export default function BlockEditorBar({
  dashboardId,
  block,
  isFirst,
  isLast,
  onBlockChanged,
  onParametersChanged,
  onMove,
  onDeleted,
}: {
  dashboardId: number;
  block: DashboardBlock;
  isFirst: boolean;
  isLast: boolean;
  onBlockChanged: (block: DashboardBlock) => void;
  onParametersChanged: (params: unknown[]) => void;
  onMove: (dir: -1 | 1) => void;
  onDeleted: () => void;
}) {
  const [refining, setRefining] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Text blocks have no query, so `refineBlock` 422s them with "Edit its text
  // directly" — which pointed at an editor that did not exist. This is it. The edit
  // is a plain PATCH of `config.body`; no model call is involved.
  // Surfacing the query is what makes a disputed number checkable. Without it, "this
  // percentage looks wrong" is unanswerable by either side — the denominator, the
  // join type and the active-employee filter are all invisible.
  const [showSql, setShowSql] = useState(false);

  /**
   * The block's own conversation. Every instruction ever given to this block, with
   * what the system did in response.
   *
   * The refine box alone showed only the CURRENT instruction, so there was no way to
   * see what had already been asked — or whether an earlier instruction had actually
   * taken effect. Loaded on demand: most viewing never opens it.
   */
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<
    Array<{ id: number; role: 'user' | 'assistant'; content: string; created_at: string }>
  >([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadHistory = async (): Promise<void> => {
    setHistoryLoading(true);
    try {
      const res = await customDashboardsAPI.messages(dashboardId, block.id);
      setHistory(res.data?.messages ?? []);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setHistoryLoading(false);
    }
  };

  // A refine can come back as questions rather than a rewritten block.
  const [questions, setQuestions] = useState<
    Array<{ id: string; question: string; options?: string[]; why?: string }>
  >([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const answered = questions.map((q) => ({
    question: q.question,
    answer: (answers[q.id] ?? '').trim(),
  }));
  const allAnswered = questions.length > 0 && answered.every((a) => a.answer.length > 0);

  const [editingText, setEditingText] = useState(false);
  const [textBody, setTextBody] = useState(
    () => (block.config as TextBlockConfig)?.body ?? ''
  );

  /** Returns whether the write succeeded, so callers can keep an editor open on failure. */
  const patch = async (
    body: Parameters<typeof customDashboardsAPI.updateBlock>[2]
  ): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      const res = await customDashboardsAPI.updateBlock(dashboardId, block.id, body);
      onBlockChanged(res.data.block as DashboardBlock);
      return true;
    } catch (e) {
      setError(errorMessage(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const submitRefine = async (clarifications: Array<{ question: string; answer: string }> = []) => {
    const text = prompt.trim();
    if (!text || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await customDashboardsAPI.refineBlock(dashboardId, block.id, text, clarifications);
      if (res.data?.status === 'needs_clarification') {
        // The model needs an answer before it can rewrite the query. Keep the
        // refine box open with the request intact and put the questions to the user.
        setQuestions(res.data.questions ?? []);
        setAnswers({});
        setBusy(false);
        return;
      }
      setQuestions([]);
      onBlockChanged(res.data.block as DashboardBlock);
      if (showHistory) void loadHistory();
      // A refinement may introduce a parameter the dashboard has never seen; the
      // server merges it, and the filter bar has to learn about it or the block's
      // {{placeholder}} would have no control feeding it.
      if (res.data.parameters) onParametersChanged(res.data.parameters);
      setPrompt('');
      setRefining(false);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Remove “${block.title ?? 'this block'}” from the dashboard?`)) return;
    setBusy(true);
    try {
      await customDashboardsAPI.removeBlock(dashboardId, block.id);
      onDeleted();
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };

  const chartConfig = block.type === 'chart' ? (block.config as ChartBlockConfig) : null;

  return (
    <div className="mt-2 border-t border-dashed border-gray-100 pt-2">
      <div className="flex flex-wrap items-center gap-1.5">
        {block.type !== 'text' ? (
          <button
            type="button"
            onClick={() => setRefining((v) => !v)}
            disabled={busy}
            className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-[11px] font-medium text-gray-600 transition hover:bg-gray-50 disabled:opacity-50"
          >
            <Wand2 className="h-3 w-3" />
            Refine
          </button>
        ) : (
          <button
            type="button"
            onClick={() => {
              // Re-seed from the block each time it opens, so a cancelled edit does
              // not leave stale text in the box on the next open.
              setTextBody((block.config as TextBlockConfig)?.body ?? '');
              setEditingText((v) => !v);
            }}
            disabled={busy}
            className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-[11px] font-medium text-gray-600 transition hover:bg-gray-50 disabled:opacity-50"
          >
            <Pencil className="h-3 w-3" />
            Edit text
          </button>
        )}

        {/* Chart type — a PATCH, not a regeneration. */}
        {chartConfig && (
          <div className="inline-flex items-center gap-0.5 rounded-md border border-gray-200 bg-white p-0.5">
            {CHART_KINDS.map((k) => (
              <button
                key={k}
                type="button"
                disabled={busy}
                onClick={() => {
                  // Pie keys off nameKey/valueKey while the others use xKey/yKey, so
                  // switching between the two families has to carry the axes across —
                  // otherwise the chart loses its columns and renders empty.
                  const c = chartConfig;
                  const x = c.xKey ?? c.nameKey;
                  const y = c.yKey ?? c.valueKey;
                  const next: ChartBlockConfig =
                    k === 'pie'
                      ? { ...c, chartType: k, nameKey: x, valueKey: y, xKey: undefined, yKey: undefined }
                      : { ...c, chartType: k, xKey: x, yKey: c.seriesKeys?.length ? undefined : y, nameKey: undefined, valueKey: undefined };
                  void patch({ config: next });
                }}
                className={`rounded px-1.5 py-0.5 text-[10px] font-medium transition ${
                  chartConfig.chartType === k
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {CHART_LABEL[k]}
              </button>
            ))}
          </div>
        )}

        <button
          type="button"
          onClick={() => {
            const next = !showHistory;
            setShowHistory(next);
            if (next) void loadHistory();
          }}
          className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium transition ${
            showHistory
              ? 'border-primary-200 bg-primary-50 text-primary-700'
              : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
          }`}
        >
          <MessageSquare className="h-3 w-3" />
          History
        </button>

        {block.generated_sql && (
          <button
            type="button"
            onClick={() => setShowSql((v) => !v)}
            className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium transition ${
              showSql
                ? 'border-primary-200 bg-primary-50 text-primary-700'
                : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Code2 className="h-3 w-3" />
            SQL
          </button>
        )}

        {/* Width — a KPI strip is always full, so the toggle is hidden for it. */}
        {block.type !== 'kpi_card_row' && (
          <button
            type="button"
            disabled={busy}
            onClick={() => void patch({ width: block.width === 'half' ? 'full' : 'half' })}
            title={block.width === 'half' ? 'Make full width' : 'Make half width'}
            className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-[11px] font-medium text-gray-600 transition hover:bg-gray-50 disabled:opacity-50"
          >
            {block.width === 'half' ? <Maximize2 className="h-3 w-3" /> : <Minimize2 className="h-3 w-3" />}
            {block.width === 'half' ? 'Full' : 'Half'}
          </button>
        )}

        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            disabled={busy || isFirst}
            onClick={() => onMove(-1)}
            title="Move up"
            className="rounded-md border border-gray-200 bg-white p-1 text-gray-500 transition hover:bg-gray-50 disabled:opacity-30"
          >
            <ChevronUp className="h-3 w-3" />
          </button>
          <button
            type="button"
            disabled={busy || isLast}
            onClick={() => onMove(1)}
            title="Move down"
            className="rounded-md border border-gray-200 bg-white p-1 text-gray-500 transition hover:bg-gray-50 disabled:opacity-30"
          >
            <ChevronDown className="h-3 w-3" />
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={remove}
            title="Remove block"
            className="rounded-md border border-rose-200 bg-white p-1 text-rose-600 transition hover:bg-rose-50 disabled:opacity-50"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      </div>

      {refining && (
        <div className="mt-2 flex items-start gap-2">
          <input
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void submitRefine();
              if (e.key === 'Escape') setRefining(false);
            }}
            disabled={busy}
            autoFocus
            placeholder="e.g. show the bottom 20 instead of 10, and add the manager name"
            className="flex-1 rounded-md border border-gray-200 px-2.5 py-1.5 text-xs focus:border-primary-500 focus:outline-none disabled:bg-gray-50"
          />
          <button
            type="button"
            onClick={() => void submitRefine()}
            disabled={busy || !prompt.trim()}
            className="inline-flex items-center gap-1 rounded-md bg-primary-600 px-2.5 py-1.5 text-xs font-medium text-white transition hover:bg-primary-700 disabled:opacity-50"
          >
            {busy && <Loader2 className="h-3 w-3 animate-spin" />}
            {busy ? 'Rewriting…' : 'Apply'}
          </button>
          <button
            type="button"
            onClick={() => setRefining(false)}
            disabled={busy}
            className="rounded-md border border-gray-200 bg-white p-1.5 text-gray-400 transition hover:bg-gray-50 disabled:opacity-50"
            aria-label="Cancel"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      )}

      {questions.length > 0 && !busy && (
        <div className="mt-2 space-y-2 rounded-md border border-primary-100 bg-primary-50/40 px-2.5 py-2">
          <p className="text-[11px] font-medium text-primary-900">
            Before I rewrite this — these change the numbers:
          </p>
          {questions.map((q) => (
            <div key={q.id}>
              <p className="text-[11px] font-medium text-gray-800">{q.question}</p>
              {q.why && <p className="text-[11px] text-gray-500">{q.why}</p>}
              {q.options && q.options.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {q.options.map((o) => (
                    <button
                      key={o}
                      type="button"
                      onClick={() => setAnswers((a) => ({ ...a, [q.id]: o }))}
                      className={`rounded border px-1.5 py-0.5 text-[10px] transition ${
                        answers[q.id] === o
                          ? 'border-primary-300 bg-primary-100 text-primary-800'
                          : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {o}
                    </button>
                  ))}
                </div>
              )}
              <input
                value={answers[q.id] ?? ''}
                onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                placeholder={q.options?.length ? 'Or type your own…' : 'Your answer…'}
                className="mt-1 w-full rounded border border-gray-200 px-2 py-1 text-[11px] focus:border-primary-500 focus:outline-none"
              />
            </div>
          ))}
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setQuestions([])}
              className="text-[10px] font-medium text-gray-500 transition hover:text-gray-700"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void submitRefine(answered)}
              disabled={!allAnswered}
              className="rounded bg-primary-600 px-2 py-1 text-[10px] font-medium text-white transition hover:bg-primary-700 disabled:opacity-50"
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {showHistory && (
        <div className="mt-2 rounded-md border border-gray-200 bg-gray-50/60 p-2">
          {historyLoading ? (
            <p className="text-[11px] text-gray-400"><InlineLoader label="Loading…" size="xs" className="flex" /></p>
          ) : history.length === 0 ? (
            <p className="text-[11px] text-gray-400">
              No history yet for this block. Instructions you give it will appear here.
            </p>
          ) : (
            <ol className="max-h-64 space-y-1.5 overflow-y-auto">
              {history.map((m) => (
                <li key={m.id} className="flex gap-2">
                  <span
                    className={`mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${
                      m.role === 'user'
                        ? 'bg-primary-100 text-primary-700'
                        : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {m.role === 'user' ? 'You' : 'System'}
                  </span>
                  <div className="min-w-0 flex-1">
                    {/* Escaped text: these turns are partly model-authored and this
                        panel is viewed by other people. */}
                    <p className="whitespace-pre-wrap break-words text-[11px] text-gray-700">
                      {m.content}
                    </p>
                    <p className="text-[10px] text-gray-400">
                      {new Date(m.created_at).toLocaleString()}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}

      {showSql && block.generated_sql && (
        <div className="mt-2">
          <p className="mb-1 text-[11px] text-gray-400">
            The exact query this block runs, after project and hierarchy scoping are applied.
            Check the denominator and the join type here before trusting a percentage.
          </p>
          <pre className="max-h-64 overflow-auto rounded-md border border-gray-200 bg-gray-50 p-2 text-[11px] leading-relaxed text-gray-700">
            {block.generated_sql}
          </pre>
        </div>
      )}

      {editingText && (
        <div className="mt-2 space-y-2">
          <textarea
            value={textBody}
            onChange={(e) => setTextBody(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') setEditingText(false); }}
            disabled={busy}
            autoFocus
            rows={3}
            placeholder="A short note to explain this dashboard…"
            className="w-full rounded-md border border-gray-200 px-2.5 py-1.5 text-xs focus:border-primary-500 focus:outline-none disabled:bg-gray-50"
          />
          <div className="flex items-center justify-between gap-2">
            {/* The body is rendered as ESCAPED text, never as markup — stated here so
                nobody expects HTML or markdown to render. */}
            <p className="text-[11px] text-gray-400">Plain text. Line breaks are kept.</p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setEditingText(false)}
                disabled={busy}
                className="rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-600 transition hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  // Only close on success — closing after a failed write would discard
                  // whatever the user had typed.
                  if (await patch({ config: { body: textBody } })) setEditingText(false);
                }}
                disabled={busy}
                className="inline-flex items-center gap-1 rounded-md bg-primary-600 px-2.5 py-1 text-xs font-medium text-white transition hover:bg-primary-700 disabled:opacity-50"
              >
                {busy && <Loader2 className="h-3 w-3 animate-spin" />}
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {error && <p className="mt-1.5 text-[11px] text-rose-600">{error}</p>}
    </div>
  );
}
